import { addVisit } from "../api/apiService";

export function patientHasNoVisits(patient) {
  if (!patient) return true;
  const raw = patient.rawData ?? patient;
  const visits = raw.visits ?? patient.visits;
  const totalVisits = raw.totalVisits ?? patient.totalVisits;
  if (Array.isArray(visits)) return visits.length === 0;
  if (typeof totalVisits === "number") return totalVisits === 0;
  return true;
}

export async function ensurePatientHasVisit(patient) {
  const existingRaw = patient.rawData ?? patient;
  const existingId = patient.id ?? patient._id ?? existingRaw._id;

  if (!patientHasNoVisits(patient)) {
    return {
      rawData: existingRaw,
      id: existingId,
    };
  }

  const patientId = existingId ?? existingRaw._id;
  if (!patientId) {
    throw new Error("Patient ID is missing. Cannot verify visit.");
  }

  throw new Error("VISIT_REQUIRED");
}

export const formatDobDisplay = (dob) => {
  if (!dob) return "";
  if (typeof dob !== "string") {
    try {
      const d = new Date(dob);
      if (!isNaN(d.getTime())) {
        return d.toISOString().split("T")[0];
      }
    } catch (e) {
      return "";
    }
  }
  const clean = String(dob).trim();
  if (
    !clean ||
    clean === "N/A" ||
    clean.toLowerCase().includes("invalid") ||
    clean.toLowerCase().includes("nan")
  ) {
    return "";
  }
  return clean.split("T")[0];
};

export const calculateAge = (dob) => {
  if (!dob) return 0;
  const cleanStr = String(dob).trim();
  if (
    !cleanStr ||
    cleanStr.toLowerCase().includes("invalid") ||
    cleanStr.toLowerCase().includes("nan")
  ) {
    return 0;
  }

  const today = new Date();
  let birthDate;

  // Try to parse DD-MM-YYYY or DD/MM/YYYY
  if (cleanStr.includes("-") || cleanStr.includes("/")) {
    const parts = cleanStr.split(/[-/]/);
    if (parts.length === 3) {
      // Check if format is DD-MM-YYYY (first part <= 31, last part length 4)
      if (
        parts[0].length <= 2 &&
        parts[2].length === 4 &&
        parseInt(parts[0], 10) <= 31
      ) {
        birthDate = new Date(
          parseInt(parts[2], 10),
          parseInt(parts[1], 10) - 1,
          parseInt(parts[0], 10)
        );
      } else if (parts[0].length === 4) {
        // Assume YYYY-MM-DD
        birthDate = new Date(cleanStr);
      }
    }
  }

  if (!birthDate || isNaN(birthDate.getTime())) {
    birthDate = new Date(cleanStr);
  }

  if (!birthDate || isNaN(birthDate.getTime())) return 0;

  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }

  if (isNaN(age) || age < 0 || age > 130) return 0;
  return age;
};

export const formatPatientName = (patient) => {
  if (!patient) return "";
  if (typeof patient === "string") return patient;
  if (patient.name) return String(patient.name);
  if (patient.fullName) return String(patient.fullName);

  const parts = [patient.f_name, patient.m_name, patient.l_name].filter(Boolean);
  if (parts.length > 0) return parts.join(" ");

  const fallbackId = patient.uhid || patient.UHID || patient._id || patient.id || "";
  return fallbackId ? `Patient #${String(fallbackId).slice(-6).toUpperCase()}` : "Patient";
};
